import { FontStyleSettings, FontStyleSettingsType } from "./types";
import { defaultDraftStyles, defaultConfirmedStyles, defaultUserStyles } from "./defaultFontStyles";

const defaultStylesMap: Record<FontStyleSettingsType, FontStyleSettings> = {
  draftStyles: defaultDraftStyles,
  confirmedStyles: defaultConfirmedStyles,
  userStyles: defaultUserStyles,
};

/**
 * Loads a named FontStyleSettings from document custom properties or fallback to default.
 */
export async function loadFontStyle(key: FontStyleSettingsType): Promise<FontStyleSettings> {
  if (typeof Word === "undefined" || !Word?.run) {
    return defaultStylesMap[key];
  }
  return await Word.run(async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();

    const item = props.getItemOrNullObject(key);
    await context.sync();

    if (!item.isNullObject) {
      item.load("value");
      await context.sync();
      try {
        const parsed = JSON.parse(item.value as string) as FontStyleSettings;
        const usesLegacyDraftDefault =
          key === "draftStyles" &&
          parsed.fontFamily === "Courier" &&
          parsed.fontSize === 10 &&
          parsed.fontColor?.toUpperCase() === "#2E8B57" &&
          parsed.bold === false &&
          parsed.italic === true &&
          !parsed.highlightColor;

        if (usesLegacyDraftDefault) {
          return { ...parsed, fontColor: defaultDraftStyles.fontColor };
        }
        return parsed as FontStyleSettings;
      } catch {
        return defaultStylesMap[key];
      }
    }
    return defaultStylesMap[key];
  });
}

/**
 * Saves a FontStyleSettings under the given key in document custom properties.
 */
export async function saveFontStyle(key: FontStyleSettingsType, settings: FontStyleSettings): Promise<void> {
  if (typeof Word === "undefined" || !Word?.run) {
    return;
  }
  await Word.run(async (context) => {
    const props = context.document.properties.customProperties;
    props.load("items");
    await context.sync();

    const existing = props.getItemOrNullObject(key);
    existing.load("isNullObject");
    await context.sync();

    const jsonString = JSON.stringify(settings);
    if (!existing.isNullObject) {
      existing.value = jsonString;
    } else {
      props.add(key, jsonString);
    }
    await context.sync();
  });
}

/**
 * Applies the named style to a Word.Range object.
 */
export async function applyFontStyle(key: FontStyleSettingsType, range: Word.Range): Promise<void> {
  const style = await loadFontStyle(key);

  const context = range.context;

  // re-attach the range to this context
  const ran = range.load("font");
  await context.sync();

  const hyperlinks = range.hyperlinks.load("items");
  await context.sync();

  const font = ran.font;
  // Apply core properties with guards to avoid invalid assignments
  if (typeof style.fontFamily === "string" && style.fontFamily) {
    font.name = style.fontFamily;
  }
  if (typeof style.fontSize === "number" && !Number.isNaN(style.fontSize)) {
    font.size = style.fontSize;
  }
  if (typeof style.fontColor === "string" && style.fontColor) {
    font.color = style.fontColor as string;
  }
  if (typeof style.highlightColor === "string" && style.highlightColor) {
    font.highlightColor = style.highlightColor as string;
  } else {
    // Strip any inherited highlight when none is configured.
    font.highlightColor = null;
  }
  // For confirmedStyles and draftStyles, preserve existing inline bold by skipping range-level bold overwrite.
  const skipEmphasis = key === "confirmedStyles" || key === "draftStyles";
  if (!skipEmphasis && typeof style.bold === "boolean") {
    font.bold = style.bold;
  }
  if (typeof style.italic === "boolean") {
    font.italic = style.italic;
  }

  if (hyperlinks.items.length > 0) {
    for (const link of hyperlinks.items) {
      link.range.styleBuiltIn = Word.BuiltInStyleName.hyperlink;
      if (typeof style.fontFamily === "string" && style.fontFamily) {
        link.range.font.name = style.fontFamily;
      }
      if (typeof style.fontSize === "number" && !Number.isNaN(style.fontSize)) {
        link.range.font.size = style.fontSize;
      }

      if (!skipEmphasis && typeof style.bold === "boolean") {
        link.range.font.bold = style.bold;
      } else {
        link.range.font.bold = false;
      }
      if (typeof style.italic === "boolean") {
        link.range.font.italic = style.italic;
      }
    }
  }

  await context.sync();
}

export function applyDraftStyle(range: Word.Range) {
  return applyFontStyle("draftStyles", range);
}

export function applyConfirmedStyle(range: Word.Range) {
  return applyFontStyle("confirmedStyles", range);
}

export function applyUserStyle(range: Word.Range) {
  return applyFontStyle("userStyles", range);
}
